import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Megaphone, Plus, Trash, X } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";

export default function Info() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const canManage = user?.role === "guru" || user?.role === "admin";

  const load = () => api.get("/announcements").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title || !form.body) return;
    try {
      await api.post("/announcements", { ...form, kelas: "Semua" });
      toast.success("Pengumuman diterbitkan");
      setForm({ title: "", body: "" });
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Yakin mau dihapus?")) return;
    await api.delete(`/announcements/${id}`);
    toast.success("Pengumuman dihapus");
    load();
  };

  return (
    <div>
      <PageHeader title="Pengumuman" back={false} />
      <div className="px-5 pt-4 space-y-3">
        {list.map((a, i) => (
          <motion.div
            key={a.id}
            data-testid={`announcement-${a.id}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className="bg-white rounded-2xl border border-stone-200/60 p-4 shadow-sm"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-secondary/20 flex items-center justify-center shrink-0">
                <Megaphone size={20} weight="fill" className="text-secondary" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-stone-800">{a.title}</p>
                <p className="text-stone-600 text-sm mt-1">{a.body}</p>
                <p className="text-stone-400 text-xs mt-2">{a.author} • {new Date(a.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "long" })}</p>
              </div>
              {canManage && (
                <button data-testid={`delete-ann-${a.id}`} onClick={() => remove(a.id)} className="text-stone-300 hover:text-destructive">
                  <Trash size={18} />
                </button>
              )}
            </div>
          </motion.div>
        ))}
        {list.length === 0 && <p className="text-center text-stone-400 mt-10">Belum ada pengumuman.</p>}
      </div>

      {canManage && (
        <button
          data-testid="add-announcement-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-1/2 translate-x-[9.5rem] md:right-auto md:translate-x-0 md:ml-[19rem] z-40 w-14 h-14 rounded-full bg-primary text-white shadow-lg flex items-center justify-center active:scale-90 transition-transform"
        >
          <Plus size={26} weight="bold" />
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setOpen(false)}>
          <motion.div
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">Buat Pengumuman</h3>
              <button onClick={() => setOpen(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            <input
              data-testid="ann-title-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Judul"
              className="w-full bg-stone-50 border border-stone-200 rounded-2xl py-3 px-4 mb-3 outline-none focus:border-primary"
            />
            <textarea
              data-testid="ann-body-input"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Isi pengumuman..."
              rows={4}
              className="w-full bg-stone-50 border border-stone-200 rounded-2xl py-3 px-4 mb-4 outline-none focus:border-primary resize-none"
            />
            <button data-testid="ann-submit-btn" onClick={submit} className="w-full bg-primary text-white font-semibold py-3.5 rounded-full active:scale-95 transition-transform">
              Terbitkan
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
